import { IMAGE_OPTIMIZER } from '../constants'
import { PendingUploadAttachedFiles, Image, UploadFile } from './response_types'
import axios from 'axios'

export const iconImageURL = (image: Image) => {
  return (
    image.url +
    `?${IMAGE_OPTIMIZER.size.icon}&token=${image.token}&optimize=medium`
  )
}

export const originalImageURL = (image: Image) => {
  return image.url + `?token=${image.token}&width=1920&optimize=medium`
}

export interface UploadFiles {
  url: string
  file: File
}

export interface SelectedFile {
  name: string
  selectedFile: File
}

/**
 * ファイルアップロードや、アップロード対象のファイルを管理
 */
export class UploadAttachedFiles {
  private imageFiles: Array<UploadFiles>
  private videoFiles: Array<UploadFiles>
  private audioFiles: Array<UploadFiles>
  private documentFiles: Array<UploadFiles>
  private otherFiles: Array<UploadFiles>
  public static readonly ImageUploadLimit = 30
  public static readonly VideoUploadLimit = 1
  public static readonly AudioUploadLimit = 10
  // Document + Zipのアップロード上限
  public static readonly OtherFileUploadLimit = 1

  public static readonly VideoByteSizeLimit = 1000 * 1000 * 1000 * 5
  public static readonly ImageByteSizeLimit = 1000 * 1000 * 20
  public static readonly AudioByteSizeLimit = 1000 * 1000 * 200
  public static readonly OtherFileByteSizeLimit = 1000 * 1000 * 200
  private static readonly VideoByteSizeLimitString = '5GB'
  public static readonly ImageByteSizeLimitString = '20MB'
  public static readonly AudioByteSizeLimitString = '200MB'
  public static readonly OtherFileSizeLimitString = '200MB'

  public static readonly ImageUploadLimitText = `一度に投稿できる画像の上限は${UploadAttachedFiles.ImageUploadLimit}点までです`
  public static readonly VideoUploadLimitText = `一度に投稿できる動画の上限は${UploadAttachedFiles.VideoUploadLimit}点までです`
  public static readonly AudioUploadLimitText = `一度に投稿できる音声ファイルの上限は${UploadAttachedFiles.AudioUploadLimit}点までです`
  public static readonly OtherFileUploadLimitText = `一度に投稿できる画像/動画/音声以外のファイル添付の上限は${UploadAttachedFiles.OtherFileUploadLimit}点までです`

  public static readonly VideoByteSizeLimitText = `${UploadAttachedFiles.VideoByteSizeLimitString}以内の動画を添付してください`
  public static readonly ImageByteSizeLimitText = `${UploadAttachedFiles.ImageByteSizeLimitString}以内の画像を添付してください`
  public static readonly AudioByteSizeLimitText = `${UploadAttachedFiles.AudioByteSizeLimitString}以内の音声ファイルを添付してください`
  public static readonly OtherFileByteSizeLimitText = `${UploadAttachedFiles.OtherFileSizeLimitString}以内のファイルを添付してください`

  /**
   * アップロードファイルの初期化
   * @param newAttachedFiles APIから返された添付ファイル
   * @param imageFiles
   * @param videoFiles
   * @param audioFiles
   * @param documentFiles
   * @param otherFiles
   */
  constructor(
    newAttachedFiles: PendingUploadAttachedFiles,
    imageFiles: Array<File>,
    videoFiles: Array<File>,
    audioFiles: Array<SelectedFile>,
    documentFiles: Array<SelectedFile>,
    otherFiles: Array<SelectedFile>
  ) {
    this.imageFiles = this.createObjectFromMapping(
      newAttachedFiles.images.presigned_urls,
      imageFiles
    )
    this.videoFiles = this.createObjectFromMapping(
      newAttachedFiles.videos.presigned_urls,
      videoFiles
    )
    this.audioFiles = this.createObjectFromReduction(
      newAttachedFiles.audios.files,
      audioFiles.map((f) => f.selectedFile)
    )
    this.documentFiles = this.createObjectFromReduction(
      newAttachedFiles.documents.files,
      documentFiles.map((f) => f.selectedFile)
    )
    this.otherFiles = this.createObjectFromReduction(
      newAttachedFiles.files.files,
      otherFiles.map((f) => f.selectedFile)
    )
  }

  /**
   * gif / png / jpeg / webp
   */
  private static readonly ImageAcceptableTypes = [
    'image/gif',
    'image/png',
    'image/jpeg',
    'image/webp'
  ]

  /**
   * 動画
   * mov / avi / mpg / m4v / mp4
   */
  private static VideoAcceptableTypes = [
    'video/mp4',
    'video/x-msvideo',
    'video/quicktime',
    'video/avi'
  ]

  /**
   * 動画
   * mov / avi / mpg / m4v / mp4
   */
  public static NotPreviewVideoTypes = ['video/avi']

  /**
   * 音声ファイル
   * wav / mp3 / AAC
   */
  private static readonly AudioAcceptableTypes = [
    'audio/wav',
    'audio/wave',
    'audio/x-wav',
    'audio/x-pn-wav',
    'audio/mpeg',
    'audio/aac'
  ]

  /**
   * ドキュメント関連
   * pdf / pptx / docx / xlsx
   */
  private static readonly DocumentAcceptableTypes = [
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ]

  private static readonly OtherAcceptableTypes = [
    'application/zip',
    'application/x-zip-compressed'
  ]

  private static readonly ImageAcceptableTypesRegex =
    UploadAttachedFiles.ImageAcceptableTypes.join('|')
  private static readonly VideoAcceptableTypesRegex =
    UploadAttachedFiles.VideoAcceptableTypes.join('|')
  private static readonly AudioAcceptableTypesRegex =
    UploadAttachedFiles.AudioAcceptableTypes.join('|')
  private static readonly DocumentAcceptableTypesRegex =
    UploadAttachedFiles.DocumentAcceptableTypes.join('|')
  private static readonly OtherAcceptableTypesRegex =
    UploadAttachedFiles.OtherAcceptableTypes.join('|')

  /**
   * 添付ファイルで許容している全種類のファイル種別を返す
   * @returns 許容するMINE種別
   */
  public static readonly DefaultAcceptableTypes: string =
    UploadAttachedFiles.ImageAcceptableTypes.concat(
      UploadAttachedFiles.VideoAcceptableTypes,
      UploadAttachedFiles.DocumentAcceptableTypes,
      UploadAttachedFiles.AudioAcceptableTypes,
      UploadAttachedFiles.OtherAcceptableTypes
    ).join()

  /**
   * 各ファイル種別にフィルタリングにした結果を返却する
   * @param files 選択されたファイルリスト
   * @returns Object
   */
  public static filterFiles(files: Array<File>) {
    return {
      images: files.filter((f) =>
        f.type.match(UploadAttachedFiles.ImageAcceptableTypesRegex)
      ),
      audios: files.filter((f) =>
        f.type.match(UploadAttachedFiles.AudioAcceptableTypesRegex)
      ),
      videos: files.filter((f) =>
        f.type.match(UploadAttachedFiles.VideoAcceptableTypesRegex)
      ),
      documents: files.filter((f) =>
        f.type.match(UploadAttachedFiles.DocumentAcceptableTypesRegex)
      ),
      others: files.filter((f) =>
        f.type.match(UploadAttachedFiles.OtherAcceptableTypesRegex)
      )
    }
  }

  public static validateLimit(
    currentSelectImageFile: Array<File>,
    selectedImageFiles: Array<File>,
    limit: number
  ) {}

  /**
   * 選択されたファイル名のユニーク化
   * 選択済みファイルに重複したファイル名が存在する場合は、ファイル名の重複回避のため末尾に(数字)を付与する
   * @param beforeSelectedFiles 選択済みファイル
   * @param nowSelectedFiles フィルタリングされた各ファイルリスト
   */
  public static filesConvertUniqueName(
    selectedFiles: Array<SelectedFile>,
    newSelectedFiles: File[]
  ): Array<SelectedFile> {
    const filemap = newSelectedFiles.map((file) => {
      let foundFile: SelectedFile | undefined
      let i = 0
      let filename = file.name
      while (true) {
        foundFile = selectedFiles.find((file) => file.name === filename)
        if (!foundFile) break
        const splitnames = file.name.split('.')
        filename = `${splitnames.shift()}(${++i}).${splitnames.join('')}`
      }
      return {
        name: filename,
        selectedFile: file
      }
    })
    filemap.unshift(...selectedFiles)
    return filemap
  }

  /**
   * ファイルの配列からファイル名のみを抽出する
   * @param files ファイルリスト
   * @returns
   */
  public static getFileNames(files: Array<SelectedFile>): Array<string> {
    return files.map((f) => f.name)
  }

  /**
   * ファイルのアップロードを実行
   * @returns ErrorDetail | null
   */
  public async uploadFiles(): Promise<null> {
    const uploadFiles = [
      ...this.imageFiles,
      ...this.videoFiles,
      ...this.audioFiles,
      ...this.documentFiles,
      ...this.otherFiles
    ]

    for (const uploadFile of uploadFiles) {
      await this.uploadFile(uploadFile)
    }
    return null
  }

  /**
   *
   * @param uploadFile
   */
  private async uploadFile(uploadFile: UploadFiles): Promise<void> {
    const config = {
      timeout: 1000 * 60 * 60 * 3, // 3時間
      headers: {
        'content-type': 'multipart/form-data'
      }
    }
    await axios.put(uploadFile.url, uploadFile.file, config)
  }

  private createObjectFromMapping(urls: Array<string>, files: Array<File>) {
    return urls.map((url: string, index: number) => {
      return { url: url, file: files[index] }
    })
  }
  private createObjectFromReduction(
    attachedFiles: Array<UploadFile>,
    files: Array<File>
  ) {
    return attachedFiles.reduce((array, attachedFile) => {
      const findFile = files.find((file) => file.name === attachedFile.filename)
      if (findFile != undefined) {
        array.push({
          url: attachedFile.presigned_url,
          file: findFile
        })
      }
      return array
    }, new Array<UploadFiles>())
  }
}
